/**
 * ESTADO DE RESULTADOS — Fase 3, paso 3.6a.
 *
 * Capa de agregación GENÉRICA sobre los `Array(12)` que hoy producen los hooks de ingreso/costo
 * (`usePublicidadCalcs`/`useContratosCalcs`/`useBTLCalcs`/`useHardwareCalcs`/`useGestionCalcs`/
 * `useRRHHCalculations`/`costosER.js` — esos hooks NO se tocan en este paso, siguen siendo la
 * fuente de los `Array(12)` de ingreso/costo; el hook del header/Acumulados del v1 se retiró
 * en el paso 3.7, al final de este archivo).
 *
 * Lo que este módulo SÍ retira (ver docs/decisiones/2026-10-04-reingenieria-ebitda-fase3-diseno.md
 * y el ajuste 1 de la revisión de Cowork): las 3 fórmulas genéricas que HOY viven duplicadas
 * en varios lugares de `index.js`/`EBITDAResultadoTable.js` con el MISMO cálculo repetido a
 * mano — EBIT/Resultado mensual, Acumulados, y la combinación Chile+Perú en US$ ("Sumar Perú").
 * Pasan a ser 1 función cada una, reusada por todos los consumidores.
 *
 * Criterio de TC: el de HOY ("último TC del mes", vía `dolarMensual[i]`) — Criterio 1 recién
 * entra en el paso 3.8. Los bugs H1-H6 (Acumulados no respeta Sumar Perú, Acumulados en US$
 * reconvierte con el TC de un solo mes, LTM desalineado, etc.) se PRESERVAN tal cual en este
 * paso — se corrigen en 3.6b, nunca en el mismo commit que los mueve (ajuste 1 de Cowork).
 */

import { convertirRegistrosAUSD } from "./conversionER";
import { INICIO_COSTOS_SII } from "./costosER";
import { agregarAMesesUSD } from "./registroFinancieroER";

/** Año desde el que hay costo del SII (facturas reales) — mismo corte que `costosER.js`. */
const ANO_INICIO_FACTURAS_COSTOS = Number(INICIO_COSTOS_SII.slice(0, 4));

/**
 * EBIT mensual = EBITDA − Depreciación − Amortización. Réplica EXACTA de la fórmula que hoy
 * vive inline en `index.js:1618` (el viejo `acumulados` useMemo, retirado en este paso) Y en
 * las 3 ramas de `EBITDAResultadoTable.js` (normal/combinado/ventana, líneas 100-114/241-257/
 * 260-268) — ahí sigue viviendo hasta que ese componente pase a recibir filas ya resueltas (más
 * adelante en este mismo paso 3.6a).
 */
export const calcularEbitMensual = (ebitdaMensual, depreciacionMensual, amortizacionMensual) =>
  Array(12)
    .fill(0)
    .map((_, i) => (ebitdaMensual[i] || 0) - (depreciacionMensual[i] || 0) - (amortizacionMensual[i] || 0));

/**
 * Resultado (Beneficio Neto) mensual = EBIT − Intereses(local) − Impuestos(USD→local, TC del
 * mes). Réplica EXACTA de `index.js:1620` (el viejo `acumulados`) y de la misma fórmula en
 * `EBITDAResultadoTable.js` (líneas 388-407/548-569/270-287 — comisión/intereses/impuestos).
 * `dolarMensualArr`/`dolarFallbackPais` SIEMPRE son los del país de SESIÓN (nunca combinados) —
 * preserva H1 a propósito: "Acumulados"/esta fórmula no miran `modoCombinado`, igual que hoy.
 */
export const calcularResultadoMensual = (
  ebitMensual,
  interesesLocalMensual,
  impuestosUSD,
  dolarMensualArr,
  dolarFallbackPais
) =>
  ebitMensual.map((ebit, i) => {
    const rate = dolarMensualArr[i] || dolarFallbackPais;
    return ebit - (interesesLocalMensual[i] || 0) - impuestosUSD * rate;
  });

/**
 * Acumulado corrido de una serie mensual en moneda LOCAL, con corte en `monthsToShow` (meses
 * futuros → `null`, nunca 0 — distingue "no llegó" de "llegó a cero"). Réplica EXACTA del loop
 * de acumulación de `index.js:1607-1630` (el viejo `acumulados` useMemo) — la conversión a US$
 * de la celda (que hoy divide el total YA acumulado por el TC de UN solo mes, el bug H2) NO
 * vive acá: sigue en `useERFormatting.formatCurrency`, sin tocar en este paso.
 */
export const acumular = (serieLocal, monthsToShow) => {
  let acum = 0;
  return serieLocal.map((v, i) => {
    if (i >= monthsToShow) return null;
    acum += v || 0;
    return acum;
  });
};

/**
 * Ventana de últimos 12 meses (LTM) — re-exporta `windowArray` (YA es fuente única en
 * `constants.js`, usada hoy vía `enVentana` en `index.js`). No se reimplementa: este módulo
 * promueve su uso a un solo lugar en vez de que cada fila de `index.js` llame `enVentana` por
 * separado (24 sitios hoy) — ver paso de retiro del `enVentana` inline, más adelante en 3.6a.
 */

/**
 * Convierte una serie mensual en moneda LOCAL a US$, mes a mes — cada mes con SU PROPIO TC
 * (nunca el TC de otro mes, nunca un promedio). Primitivo compartido por `combinarEnUSD` (abajo)
 * y por el fix de H2/H5 (PASO 3.6b): "Acumulados en US$"/"LTM en US$" tienen que sumar/mostrar
 * cada mes YA convertido, nunca reconvertir un total ya acumulado con el TC de un solo mes.
 */
export const convertirSerieMensualAUSD = (serieLocal, tcMensual, tcFallback) =>
  Array(12)
    .fill(0)
    .map((_, i) => {
      const tc = tcMensual[i] || tcFallback || 1;
      return (serieLocal[i] || 0) / tc;
    });

/**
 * Combina Chile + Perú en US$ ("Sumar Perú") — réplica EXACTA del patrón que hoy se duplica a
 * mano en `aUSD` (`index.js:1138-1146`), `enUSDPorPais` (`index.js:1645-1653`),
 * `costosVariablesCombinadosUSD`/`costosFijosCombinadosUSD` y cada hook de ingreso
 * (`publicidadMensualesCombinados`/`contratosMensualesCombinados`/`gestionMensualesCombinados`,
 * etc.): cada país se divide por SU PROPIO TC mensual y se suman los US$ resultantes — nunca un
 * TC único ni una suma de moneda local entre países (10.185).
 */
export const combinarEnUSD = (serieChile, seriePeru, dolarMensualChile, dolarMensualPeru, dolarFallback) => {
  const chileUSD = convertirSerieMensualAUSD(serieChile, dolarMensualChile, dolarFallback?.Chile);
  const peruUSD = convertirSerieMensualAUSD(seriePeru, dolarMensualPeru, dolarFallback?.["Perú"]);
  return chileUSD.map((v, i) => v + (peruUSD[i] || 0));
};

/**
 * Acumulado corrido en US$ (PASO 3.6b, fix H2/H5): suma cada mes YA convertido a su propio TC,
 * nunca el acumulado en pesos dividido por el TC de un solo mes. Mismo corte en `monthsToShow`
 * que `acumular` (meses futuros → `null`).
 */
export const acumularEnUSD = (serieLocal, tcMensual, tcFallback, monthsToShow) =>
  acumular(convertirSerieMensualAUSD(serieLocal, tcMensual, tcFallback), monthsToShow);

export { windowArray as ventana } from "./constants";

/**
 * PASO 3 (Criterio 1), punto 3 — UNIFICACIÓN del estimado de software de cartelería.
 *
 * Hasta este punto había DOS fórmulas divergentes para el mismo hecho ("pantallas activas del
 * mes × US$10.99"): `calcularCostoCarteleriaEstimado` (vivía acá, usada SOLO por el camino
 * combinado/región de `index.js`) y `calcularCostoSoftwareEstimado` (`useGestionCalcs.js`,
 * usada por el modo normal de un solo país). Documentado como hallazgo desde 3.6a (10.243,
 * "mover ≠ corregir" — no se tocó entonces), medido y corregido ACÁ:
 *
 * 1. Conteo de pantallas: `calcularCostoCarteleriaEstimado` SIEMPRE proyectaba (nunca miraba
 *    `meses_facturacion`); `calcularPantallasActivasPorMes` (abajo) SÍ tiene un camino
 *    "facturado real" cuando el negocio tiene cuotas de facturación cargadas — pantallas REALES
 *    del mes, no una proyección. Se elige ESE camino (gana la que "usa pantallas... reales del
 *    mes", punto 3 del encargo).
 * 2. El camino de REPUESTO de `calcularPantallasActivasPorMes` (sin `meses_facturacion`) tenía
 *    un bug real: solo chequeaba `fecha_inicio <= fin de mes`, SIN tope superior — un negocio
 *    terminado seguía contando pantallas para siempre. `calcularCostoCarteleriaEstimado` SÍ
 *    respetaba `plazo_contrato_meses` como tope. Se PORTA ese tope al camino de repuesto (fix
 *    real, no solo mover) — ver `calcularPantallasActivasPorMes` abajo.
 * 3. TC: se unifica en `getDolarValue(fechaDia1)` (día 1 exacto del mes) — gana sobre el array
 *    `dolarPais[i]` (último TC disponible del mes) porque es la tarifa REAL de un día concreto,
 *    no un valor ya resuelto para otro propósito (punto 3 del encargo: "...y tarifa reales del
 *    mes"). Mismo mecanismo que ya usaba `calcularCostoSoftwareEstimado`.
 * 4. Fix adicional, encontrado al unificar: el "año anterior" de `useGestionCalcs.js`
 *    (`calcularCostoSoftwareEstimadoAnoAnterior`) armaba la fecha del TC con `currentYear` (el
 *    año de HOY) en vez del año cuyas pantallas está contando (`selectedYear - 1`) — un bug
 *    real, aislado (10.64), corregido acá de una vez porque la función unificada recibe `ano`
 *    explícito y lo usa para AMBAS cosas (pantallas y fecha del TC).
 */

/**
 * `meses_facturacion` → array[12] de "cuántas cuotas gestión/contenidos cayeron en cada mes",
 * o `null` si el negocio no tiene el campo poblado (cae al camino de repuesto). Réplica EXACTA
 * de `getCuotasFacturadasPorMes` (`useGestionCalcs.js`, movida acá — 10.1, un solo lugar para
 * el conteo de pantallas que usan `calcularPantallasActivasPorMes`/`detalleGestionContenidosPorMes`).
 */
export const getCuotasFacturadasPorMes = (negocio, year) => {
  const entries = Array.isArray(negocio.meses_facturacion) ? negocio.meses_facturacion : [];
  if (entries.length === 0) return null;
  const incluyeContenidos = !!negocio.incluye_contenidos;
  const result = Array.from({ length: 12 }, () => ({ gestion: 0, contenidos: 0, fechaFactura: null }));
  let hasAny = false;
  entries.forEach((e) => {
    if (!e || !e.facturafecha) return;
    if (e.tipo !== "gestion" && e.tipo !== "contenidos") return;
    const [fy, fm] = e.facturafecha.split("-").map(Number);
    if (fy !== year) return;
    const idx = fm - 1;
    if (idx < 0 || idx > 11) return;
    result[idx][e.tipo] += 1;
    result[idx].fechaFactura = e.facturafecha;
    hasAny = true;
  });
  if (incluyeContenidos) {
    const contInicio = negocio.contenidos_fecha_inicio ? new Date(negocio.contenidos_fecha_inicio + "T00:00:00") : null;
    const contFin = negocio.contenidos_fecha_fin ? new Date(negocio.contenidos_fecha_fin + "T00:00:00") : null;
    result.forEach((slot, idx) => {
      if (slot.gestion > 0 && slot.contenidos === 0) {
        const slotDate = new Date(year, idx, 1);
        const enRango = (!contInicio || slotDate >= contInicio) && (!contFin || slotDate <= contFin);
        if (enRango) slot.contenidos = slot.gestion;
      }
    });
  }
  return hasAny ? result : null;
};

/**
 * Pantallas activas por mes, para un año dado — camino "facturado real" (`meses_facturacion`)
 * si existe, camino de repuesto (`fecha_inicio`/`plazo_contrato_meses`, CON tope superior —
 * punto 2 del hallazgo arriba) si no. Réplica de `calcularPantallasActivasPorMes`
 * (`useGestionCalcs.js`), con el fix del tope superior portado desde la vieja
 * `calcularCostoCarteleriaEstimado`.
 */
export const calcularPantallasActivasPorMes = (negocios, year) => {
  const pantallasPorMes = Array(12).fill(0);
  (negocios || []).forEach((negocio) => {
    if (negocio.eliminada || negocio.estado !== "Activo" || !negocio.fecha_inicio) return;
    const fechaInicio = new Date(negocio.fecha_inicio);
    const plazoMeses = parseInt(negocio.plazo_contrato_meses) || 0;
    if (plazoMeses === 0) return;
    const pantallas = parseInt(negocio.cantidad_pantallas) || 1;
    const cuotasFacturadas = getCuotasFacturadasPorMes(negocio, year);
    if (cuotasFacturadas) {
      cuotasFacturadas.forEach((slot, m) => {
        if (slot.gestion === 0 && slot.contenidos === 0) return;
        pantallasPorMes[m] += pantallas;
      });
      return;
    }
    // Camino de repuesto — FIX (punto 2 del hallazgo): tope superior `fechaInicio + plazoMeses`,
    // portado de la vieja `calcularCostoCarteleriaEstimado`. Antes: `fechaInicio <= finDeMes`,
    // sin tope — un contrato terminado seguía contando pantallas para siempre.
    const fechaFin = new Date(fechaInicio);
    fechaFin.setMonth(fechaInicio.getMonth() + plazoMeses);
    for (let m = 0; m < 12; m++) {
      const medioDelMes = new Date(year, m, 15);
      if (medioDelMes >= fechaInicio && medioDelMes < fechaFin) pantallasPorMes[m] += pantallas;
    }
  });
  return pantallasPorMes;
};

/**
 * Función ÚNICA (punto 3 del encargo) — reemplaza `calcularCostoCarteleriaEstimado` (camino
 * combinado/región) Y la fórmula inline de `calcularCostoSoftwareEstimado`/
 * `calcularCostoSoftwareEstimadoAnoAnterior` (`useGestionCalcs.js`, modo normal) — usada por
 * TODOS los caminos desde este punto.
 *
 * AJUSTE de Cowork (2026-10-06, sobre el punto 3): el TC NO es el del día 1 — este estimado es
 * un monto SIN FECHA PROPIA (no es una factura real, es una proyección mensual), así que le
 * corresponde el Criterio 1b (decisión 2026-10-04): el TC PROMEDIO real del mes
 * (`calcularDolarMensualPromedio`), igual que cualquier otro registro sin fecha propia — nunca
 * un día puntual. Antes (commit 68ce6bec) usaba el día 1 exacto, que no es ninguno de los dos
 * criterios de la decisión.
 *
 * @param {Array} negocios - `negocios_carteleria` del país.
 * @param {number} ano - Año de las pantallas Y del array de TC (antes podían ser dos años
 *   distintos para el "año anterior" — punto 4 del hallazgo arriba).
 * @param {number} mesIndice - 0-11.
 * @param {Array} dolarMensualPromedioArr - Array(12) de `calcularDolarMensualPromedio(pais, ano)`
 *   — el `ano` del array tiene que ser el MISMO `ano` de este llamado (Criterio 1b, mes a mes).
 * @returns {number} Costo de cartelería estimado del mes, en moneda local.
 */
export const calcularCostoSoftwareCarteleria = (negocios, ano, mesIndice, dolarMensualPromedioArr) => {
  const pantallas = calcularPantallasActivasPorMes(negocios, ano)[mesIndice] || 0;
  if (pantallas === 0) return 0;
  const dolar = dolarMensualPromedioArr?.[mesIndice] || 0;
  return Math.round(pantallas * 10.99 * dolar);
};

/**
 * FASE 3 PASO 3 (criterio 1), punto 4 — US$ de una cuenta de costo del SII (Servicios
 * internet/Mantenimiento/Compra hardware), Chile, con el MISMO corte de fuente que ya usa la
 * fila en pesos (`porAno`): desde `ANO_INICIO_FACTURAS_COSTOS` lee `costos.registros` (cada
 * factura con su FECHA REAL — Criterio 1a); antes, la carga manual no tiene fecha propia
 * (Criterio 1b, TC promedio del mes). El monto en PESOS no cambia (se sigue leyendo de
 * `costos.porCuenta(cuenta)`/la serie manual, intacto) — esto solo construye el US$ bottom-up,
 * nunca reconvierte el total.
 *
 * @param {object} p
 * @param {{registros: Array}} p.costos - Retorno de `agregarCostosER` (costosER.js).
 * @param {string} p.cuenta - `cuenta_er` de la fila (p.ej. "servicios_internet").
 * @param {"Chile"|"Perú"} p.pais
 * @param {number} p.ano
 * @param {Array} p.manualPesoPorMes - Serie manual en pesos (fuente de la fila antes de 2026).
 * @param {Array} p.dolarMensualPromedioArr - `calcularDolarMensualPromedio(pais, ano)`.
 * @returns {Array} US$ por mes (12).
 */
export const usdCuentaCostoConFallbackManual = ({
  costos,
  cuenta,
  pais,
  ano,
  manualPesoPorMes,
  dolarMensualPromedioArr,
}) => {
  if (ano >= ANO_INICIO_FACTURAS_COSTOS) {
    const regs = (costos?.registros || []).filter((r) => r.concepto === cuenta && r.pais === pais && r.ano === ano);
    return agregarAMesesUSD(convertirRegistrosAUSD(regs), { pais, ano });
  }
  return Array.from({ length: 12 }, (_, i) => {
    const tc = dolarMensualPromedioArr?.[i] || 0;
    const monto = manualPesoPorMes?.[i] || 0;
    return tc > 0 ? monto / tc : 0;
  });
};

/**
 * FASE 3 PASO 3 (criterio 1), punto 4 — US$ de una serie 100% manual, sin integración de
 * facturas en ningún año (Instalación/Materiales/Otros costos B-C-D, y el reparto de
 * Contenidos Digitales A/C): SIEMPRE Criterio 1b (TC promedio del mes) — no tiene fecha propia
 * en ningún régimen, a diferencia de las cuentas con facturas de arriba.
 */
export const usdSerieManual = ({ serieMensual, dolarMensualPromedioArr }) =>
  Array.from({ length: 12 }, (_, i) => {
    const tc = dolarMensualPromedioArr?.[i] || 0;
    const monto = serieMensual?.[i] || 0;
    return tc > 0 ? monto / tc : 0;
  });

/**
 * FASE 3 PASO 3 (criterio 1), punto 4b — US$ de "Software DS" (A). Pesos SIN CAMBIO (siguen
 * siendo `max(0, facturas − estimado)`, calculado donde siempre — este helper no lo toca). US$:
 *  - Si el residuo en pesos de ese mes es 0 (el estimado ganó el `max(0, …)`): US$ = 0 — no
 *    tiene sentido mostrar un US$ de algo que la fila de pesos ya aplanó a 0.
 *  - Si el residuo es > 0 (las facturas reales superan el estimado): US$ = Σ(factura
 *    convertida a su propia fecha, Criterio 1a) − el estimado en su dólar NATIVO (pantallas ×
 *    10.99, sin ida y vuelta por pesos — punto 4c aplica el mismo principio en C).
 *  - Antes de 2026 (carga manual, sin facturas ni dólar nativo): Criterio 1b sobre el residuo.
 */
export const usdSoftwareDSA = ({ costos, pais, ano, negocios, pesoResidualPorMes, dolarMensualPromedioArr }) => {
  const pantallasPorMes = calcularPantallasActivasPorMes(negocios, ano);
  return Array.from({ length: 12 }, (_, i) => {
    const residual = pesoResidualPorMes?.[i] || 0;
    if (residual <= 0) return 0;
    if (ano < ANO_INICIO_FACTURAS_COSTOS) {
      const tc = dolarMensualPromedioArr?.[i] || 0;
      return tc > 0 ? residual / tc : 0;
    }
    const regs = (costos?.registros || []).filter(
      (r) => r.concepto === "software_ds" && r.pais === pais && r.ano === ano && r.mesIndice === i
    );
    const sumaUsdFacturas = convertirRegistrosAUSD(regs).reduce((acc, r) => acc + (r.montoUSD || 0), 0);
    const estimadoUsdNativo = (pantallasPorMes[i] || 0) * 10.99;
    return sumaUsdFacturas - estimadoUsdNativo;
  });
};

/**
 * FASE 3 PASO 3 (criterio 1), punto 4c — US$ de "Software DS" (C). Pesos SIN CAMBIO (override
 * manual si > 0, si no el estimado — como ya hace `softwareCarteleria`). US$:
 *  - Override manual (> 0, solo posible antes de 2026 — `getCostoGestionValue` corta en
 *    `ANO_INICIO_FACTURAS`): Criterio 1b, TC promedio del mes (no tiene fecha propia).
 *  - Estimado (el caso de siempre desde 2026): su valor dólar NATIVO (pantallas × 10.99),
 *    SIN ida y vuelta por pesos — es la definición misma del estimado, no una conversión.
 */
export const usdSoftwareDSC = ({ negocios, ano, manualPesoPorMes, dolarMensualPromedioArr }) => {
  const pantallasPorMes = calcularPantallasActivasPorMes(negocios, ano);
  return Array.from({ length: 12 }, (_, i) => {
    const manualPeso = manualPesoPorMes?.[i] || 0;
    if (manualPeso > 0) {
      const tc = dolarMensualPromedioArr?.[i] || 0;
      return tc > 0 ? manualPeso / tc : 0;
    }
    return (pantallasPorMes[i] || 0) * 10.99;
  });
};

/**
 * PASO 3.6a — retiro del inline de "Hardware" (P2.115, `abrirHardwareIngresos`, antes
 * `index.js:1908-1987`) — construye la lista de negocios que componen el ingreso de Hardware de
 * un mes, para el modal de detalle (`DetalleHardwareIngresosModal`). Extraído VERBATIM.
 *
 * **Hallazgo real al intentar la retirada (10.243): esta función NO es equivalente a
 * `ingresosER.registrosHardware` (paso 3.3) ni a `useHardwareCalcs.calcIngresosHardware`:**
 * el camino "facturado real" (`meses_facturacion` con `tipo==="opex"`) se chequea ACÁ de forma
 * INCONDICIONAL (antes de mirar `n.modalidad`) — en `registrosHardware`/`useHardwareCalcs` ese
 * camino vive DENTRO de la rama `modalidad==="OPEX" && cuota_hardware_opex && fecha_cierre`, así
 * que un negocio sin esos 2 campos pero con `meses_facturacion` tipo "opex" aparecería en este
 * modal y NO en el ingreso agregado de la tabla — divergencia real, no solo de forma. Tampoco
 * usa `fechaLocalDeNegocio`/`parseLocalYMD` (H7) en ningún `new Date(...)` — se preserva tal
 * cual, no se corrige acá (ningún número se re-computa, es un drill-down de lectura).
 *
 * @param {Array} negociosCarteleria - Del país de sesión.
 * @param {number} selectedYear
 * @param {number} monthIndex
 * @returns {Array} Negocios (con `_cuotasEnMes`/`_facturaFecha`/`_montoTramoMes`) del mes.
 */
export const resolverNegociosHardwareDelMes = (negociosCarteleria, selectedYear, monthIndex) =>
  (negociosCarteleria || [])
    .map((n) => {
      if (!n.incluye_hardware || n.eliminada) return null;
      if (!["100%", "Activo"].includes(n.estado)) return null;

      const mesesFact = Array.isArray(n.meses_facturacion) ? n.meses_facturacion : [];
      const opexEntries = mesesFact.filter((e) => e && e.tipo === "opex" && e.facturafecha);
      if (opexEntries.length > 0) {
        const cuotasDelMes = opexEntries.filter((e) => {
          const [fy, fm] = e.facturafecha.split("-").map(Number);
          return fy === selectedYear && fm - 1 === monthIndex;
        });
        if (cuotasDelMes.length === 0) return null;
        return { ...n, _cuotasEnMes: cuotasDelMes.length, _facturaFecha: cuotasDelMes[0].facturafecha };
      }

      if (n.modalidad === "OPEX" && n.cuota_hardware_opex && n.fecha_cierre) {
        const fechaCierre = new Date(n.fecha_cierre);
        const mesCierre = fechaCierre.getMonth();
        const añoCierre = fechaCierre.getFullYear();
        if (añoCierre > selectedYear) return null;
        const totalCuotas = Number(n.num_cuotas) || Number(n.plazo_contrato_meses) || 36;
        const mesesTranscurridos = (selectedYear - añoCierre) * 12 + (monthIndex - mesCierre);
        if (mesesTranscurridos >= 0 && mesesTranscurridos < totalCuotas) {
          return { ...n, _cuotasEnMes: 1, _facturaFecha: null };
        }
        return null;
      }

      if (n.modalidad === "CAPEX") {
        const detalleCapex = Array.isArray(n.detalle_pagos_hardware) ? n.detalle_pagos_hardware : [];
        const detalleOtros = Array.isArray(n.detalle_otros_hardware) ? n.detalle_otros_hardware : [];
        const tieneDesglose = detalleCapex.some((p) => p.monto != null);

        const tramosDelMes = detalleCapex.filter((p) => {
          const fechaTramo = p.facturafecha || p.fecha_factura;
          if (!fechaTramo || p.monto == null) return false;
          const f = new Date(fechaTramo);
          return f.getMonth() === monthIndex && f.getFullYear() === selectedYear;
        });
        const otrosDelMes = detalleOtros.filter((p) => {
          if (!p.fecha_factura || p.monto == null) return false;
          const f = new Date(p.fecha_factura);
          return f.getMonth() === monthIndex && f.getFullYear() === selectedYear;
        });

        if (tramosDelMes.length > 0 || otrosDelMes.length > 0) {
          const _montoTramoMes =
            tramosDelMes.reduce((s, p) => s + (Number(p.monto) || 0), 0) +
            otrosDelMes.reduce((s, p) => s + (Number(p.monto) || 0), 0);
          const _facturaFecha =
            tramosDelMes[0]?.facturafecha || tramosDelMes[0]?.fecha_factura || otrosDelMes[0]?.fecha_factura || null;
          return { ...n, _cuotasEnMes: 1, _facturaFecha, _montoTramoMes };
        }

        const tieneFechaCierre =
          !tieneDesglose &&
          n.fecha_cierre &&
          new Date(n.fecha_cierre).getMonth() === monthIndex &&
          new Date(n.fecha_cierre).getFullYear() === selectedYear;
        if (tieneFechaCierre) {
          return { ...n, _cuotasEnMes: 1, _facturaFecha: n.fecha_cierre };
        }
        return null;
      }

      return null;
    })
    .filter(Boolean);

/**
 * PASO 3.7 — EBITDA mensual = Facturación − Costos Variables − Costos Fijos. Réplica EXACTA
 * de la fórmula interna que el hook v1 (retirado en este paso) armaba con
 * su propio `facturacionMensual`/`costosVariablesMensual`/`sumarCostosFijosMes` — probado
 * término a término idéntico a `facturacion`/`costosVariables`/`costosFijos` del v2
 * (`docs/decisiones/2026-10-01-erv2-ebitda-acumulado-header-fuente-unica.md`), así que esta
 * función recibe esos 3 arrays DIRECTO, sin reconstruir nada. `monthsToShow=12` (año anterior,
 * siempre completo) nunca zera ningún mes; el año en curso sigue zerando `i >= monthsToShow`
 * igual que el hook.
 */
export const calcularEbitdaMensual = (facturacion, costosVariables, costosFijos, monthsToShow = 12) =>
  Array(12)
    .fill(0)
    .map((_, i) => (i >= monthsToShow ? 0 : (facturacion[i] || 0) - (costosVariables[i] || 0) - (costosFijos[i] || 0)));

/**
 * PASO 3.7 — EBITDA mensual del AÑO ANTERIOR: mismo `calcularEbitdaMensual`, con `monthsToShow`
 * SIEMPRE en 12 (el año anterior está siempre completo, nunca zera ningún mes) — nombre propio
 * en vez de un `calcularEbitdaMensual(..., 12)` inline para que el test de invariante
 * (`fase1bInvarianteAnoAnterior.test.js`) pueda interceptar ESTA llamada sin ambigüedad frente
 * a la del año en curso (que comparte la misma función genérica de abajo).
 */
export const calcularEbitdaMensualAnoAnterior = (
  facturacionAnoAnterior,
  costosVariablesAnoAnterior,
  costosFijosAnoAnterior
) => calcularEbitdaMensual(facturacionAnoAnterior, costosVariablesAnoAnterior, costosFijosAnoAnterior, 12);

/**
 * PASO 3.7 — Costos Variables del AÑO ANTERIOR. Único término sin equivalente ya existente en
 * `index.js` (a diferencia de `facturacion`/`costosVariables`/`costosFijos` del año en curso):
 * `costosVariablesAA` (H6/LTM, `index.js`) usa una fuente de hardware/software DISTINTA
 * (`costosAA.porCuenta("hardware")` entero, sin desglose; sin fallback de software estimado) y
 * le faltan comisión broker/take/APX/otros-BTL — no es intercambiable para este invariante.
 * Réplica VERBATIM del hook v1 retirado (3.7), líneas 172-239, misma fórmula, mismos 11 términos,
 * parametrizada en vez de recibida por destructuring de props de hook.
 */
export const calcularCostosVariablesAnoAnterior = ({
  costosPublicidadDataAnoAnterior,
  calcularCostoSoftwareEstimadoAnoAnterior,
  valoresMensualesComisionAgenciaAnoAnterior,
  valoresMensualesComisionBrokerAnoAnterior,
  valoresMensualesComisionesAnoAnterior,
  valoresMensualesComisionComercialAnoAnterior,
  valoresMensualesTakeAnoAnterior,
  valoresMensualesApxAnoAnterior,
  compraHardwareMensualAnoAnterior,
  instalacionMensualAnoAnterior,
  materialesMensualAnoAnterior,
  otrosCostosMensualAnoAnterior,
  getCostoGestionValueAnoAnterior,
  comisionEdificiosBTLMensualAnoAnterior,
  otrosCostosBTLMensualAnoAnterior,
}) =>
  Array(12)
    .fill(0)
    .map((_, i) => {
      const cpAA = costosPublicidadDataAnoAnterior[i] || {};
      const rawSoftwareDS = Number(cpAA.software_ds) || 0;
      const costoCarteleria = calcularCostoSoftwareEstimadoAnoAnterior(i);
      const softwareDSAjustado = Math.max(0, rawSoftwareDS - costoCarteleria);
      const conceptosManualesAA =
        (Number(cpAA.servicios_internet) || 0) + softwareDSAjustado + (Number(cpAA.mantenimiento) || 0);
      const comAg = valoresMensualesComisionAgenciaAnoAnterior[i] || 0;
      const comBrokerAA = valoresMensualesComisionBrokerAnoAnterior[i] || 0;
      const comEd = valoresMensualesComisionesAnoAnterior[i] || 0;
      const comCom = valoresMensualesComisionComercialAnoAnterior[i] || 0;
      const takeAA = (valoresMensualesTakeAnoAnterior && valoresMensualesTakeAnoAnterior[i]) || 0;
      const apxAA = (valoresMensualesApxAnoAnterior && valoresMensualesApxAnoAnterior[i]) || 0;
      const hw =
        (compraHardwareMensualAnoAnterior[i] || 0) +
        (instalacionMensualAnoAnterior[i] || 0) +
        (materialesMensualAnoAnterior[i] || 0) +
        (otrosCostosMensualAnoAnterior[i] || 0);
      const swGestionSupabase = getCostoGestionValueAnoAnterior(i, "software_ds") || 0;
      const swGestion = swGestionSupabase > 0 ? swGestionSupabase : calcularCostoSoftwareEstimadoAnoAnterior(i);
      const otrosGestion = getCostoGestionValueAnoAnterior(i, "otros") || 0;
      const btl = (comisionEdificiosBTLMensualAnoAnterior[i] || 0) + (otrosCostosBTLMensualAnoAnterior[i] || 0);
      return (
        comAg +
        comBrokerAA +
        comEd +
        comCom +
        takeAA +
        apxAA +
        conceptosManualesAA +
        hw +
        swGestion +
        otrosGestion +
        btl
      );
    });

/**
 * PASO 3.7 — suma los últimos `n` meses de una serie mensual, cruzando al año anterior cuando
 * `monthsToShow - n < 0` (ej. Run Rate trimestral en enero/febrero). Réplica de
 * `factUltimos3`/`ebitdaUltimos3`/`facturacionUltimos12Meses`/`ebitdaUltimos12Meses`, las 4 del
 * hook v1 retirado (3.7) — eran la MISMA estructura de loop con `n` distinto (3 o 12); se
 * unifican en un solo primitivo reusado con el `n` que corresponda.
 */
export const sumarUltimosNMeses = (serieActual, serieAnoAnterior, monthsToShow, n) => {
  let suma = 0;
  for (let offset = 0; offset < n; offset++) {
    const mesRelativo = monthsToShow - 1 - offset;
    suma += mesRelativo >= 0 ? serieActual[mesRelativo] || 0 : serieAnoAnterior[12 + mesRelativo] || 0;
  }
  return suma;
};

/**
 * PASO 3.7 — igual que `sumarUltimosNMeses`, pero cada mes se convierte a US$ con el TC de SU
 * propio mes ANTES de sumar (Run Rate en dólares). Réplica de `runRateAnualUSD`/
 * `ebitdaRunRateAnualUSD` del hook v1 retirado (3.7).
 */
export const sumarUltimosNMesesEnUSD = (
  serieActual,
  serieAnoAnterior,
  tcActual,
  tcAnoAnterior,
  tcFallback,
  monthsToShow,
  n
) => {
  let suma = 0;
  for (let offset = 0; offset < n; offset++) {
    const mesRelativo = monthsToShow - 1 - offset;
    if (mesRelativo >= 0) {
      const tc = tcActual[mesRelativo] || tcFallback || 1;
      suma += (serieActual[mesRelativo] || 0) / tc;
    } else {
      const mesAA = 12 + mesRelativo;
      const tc = tcAnoAnterior[mesAA] || tcFallback || 1;
      suma += (serieAnoAnterior[mesAA] || 0) / tc;
    }
  }
  return suma;
};

/**
 * PASO 3.7 — suma los últimos `n` meses de una serie YA combinada (modo región), SIN cruzar al
 * año anterior (índices negativos se ignoran — la región no tiene un array "año anterior"
 * propio). Réplica de `runRateAnualRegion`/`ebitdaRunRateAnualRegion` del hook v1 retirado (3.7).
 */
export const sumarUltimosNMesesSimple = (serie, monthsToShow, n) => {
  let suma = 0;
  for (let i = monthsToShow - n; i < monthsToShow; i++) {
    if (i >= 0) suma += serie[i] || 0;
  }
  return suma;
};

/**
 * PASO 3.7 — YTD de una serie (primeros `monthsToShow` meses), anualizado a 12 meses. Réplica
 * de `ytdFacturacionRegion`/`ytdAnualizado` y `ytdEbitdaRegion`/`ebitdaAnualizado` del hook v1
 * retirado (3.7), modo región — en ambos casos el YTD de una resta (EBITDA = Facturación
 * − Costos) es la resta de los YTD, así que un solo primitivo sirve para los dos.
 */
export const calcularYtdAnualizado = (serie, monthsToShow, mesesTranscurridos) => {
  const ytd = serie.slice(0, monthsToShow).reduce((a, b) => a + (b || 0), 0);
  return mesesTranscurridos > 0 ? Math.round((ytd * 12) / mesesTranscurridos) : 0;
};

/**
 * PASO 3.7 — variación porcentual clásica (actual vs. base), `null` si la base es 0. Réplica de
 * `variacionVsPropioRitmo`/`variacionEbitdaRunRateRegion` del hook v1 retirado (3.7).
 */
export const calcularVariacionPorcentual = (actual, base) =>
  base === 0 ? null : ((actual - base) / Math.abs(base)) * 100;

/**
 * PASO 3.7 — variación en forma de RATIO (`actual/base − 1`, ×100), 0 (no `null`) si la base no
 * es positiva. Réplica de `variacionRunRateRegion` del hook v1 retirado (3.7) — misma idea que
 * `calcularVariacionPorcentual` pero con la base en el denominador en vez de en la resta, y el
 * guard devuelve 0 en vez de `null` (preservado tal cual, no es un error: así se comportaba el
 * hook).
 */
export const calcularVariacionRatio = (actual, base) => (base > 0 ? (actual / base - 1) * 100 : 0);

/**
 * FASE E1 (2026-10-05, orden nuevo de la revisión de Cowork —
 * docs/decisiones/2026-10-05-reingenieria-ebitda-fase3-paso-3-8-revision-cowork.md).
 *
 * Estructura `{ [label]: { local: Array(12), usd: Array(12) } }` con TODAS las filas visibles
 * del ER v2, para UN país. Criterio de TC: el de HOY (igual que hoy muestra la pantalla en
 * "Ver en US$") — Criterio 1 recién entra en las fases C1-C6. SIN consumidor en `index.js`
 * todavía — solo estas funciones puras + su test de paridad.
 *
 * Las filas HOJA (`leaves`, parámetro) llegan YA resueltas — lo mismo que hoy usa la pantalla,
 * en ambas monedas — esta función NUNCA las recalcula. Las filas SUMA se calculan ÚNICAMENTE
 * sumando/restando filas ya puestas en la estructura, nunca aparte (regla 1 del prompt E1) —
 * así es estructuralmente imposible que un total no sea la suma de lo que se ve, el defecto
 * que hizo revertir la entrega anterior (`71eac47c`).
 */

const sumarFilas = (...filas) => ({
  local: Array(12)
    .fill(0)
    .map((_, i) => filas.reduce((acc, f) => acc + (f?.local?.[i] || 0), 0)),
  usd: Array(12)
    .fill(0)
    .map((_, i) => filas.reduce((acc, f) => acc + (f?.usd?.[i] || 0), 0)),
});

const restarFilas = (base, ...filas) => ({
  local: Array(12)
    .fill(0)
    .map((_, i) => (base?.local?.[i] || 0) - filas.reduce((acc, f) => acc + (f?.local?.[i] || 0), 0)),
  usd: Array(12)
    .fill(0)
    .map((_, i) => (base?.usd?.[i] || 0) - filas.reduce((acc, f) => acc + (f?.usd?.[i] || 0), 0)),
});

/** Las 13 cuentas de Costos Fijos, en el mismo orden que `nombresCostosFijosV2` (`constants.js`). */
export const NOMBRES_COSTOS_FIJOS_ER = [
  "Oficina",
  "Transporte",
  "Telefonía",
  "Marketing",
  "Contador",
  "Abogados",
  "Gastos representación",
  "Asesoría externa",
  "Alojamiento",
  "Viajes Gerencia",
  "Gastos Financieros",
  "Varios",
  "Servicios Online",
];

/** Toda fila-hoja que `construirFilasER` espera recibida, por bloque — usado para validar (10.177). */
export const HOJAS_ER = [
  "A - Publicidad",
  "A - Publicidad / Costo intermediación",
  "A - Publicidad / Comisión edificios",
  "A - Publicidad / Comisión comercial",
  "A - Publicidad / Servicios internet",
  "A - Publicidad / Software DS",
  "A - Publicidad / Mantenimiento",
  "A - Publicidad / Contenidos Digitales",
  "B - Hardware",
  "B - Hardware / Compra hardware",
  "B - Hardware / Instalación",
  "B - Hardware / Materiales",
  "B - Hardware / Otros costos",
  "C - Gestión & Contenidos",
  "C - Gestión & Contenidos / Software DS",
  "C - Gestión & Contenidos / Otros",
  "C - Gestión & Contenidos / Contenidos Digitales",
  "D - BTL & otros",
  "D - BTL & otros / Comisión edificios",
  "D - BTL & otros / Otros costos",
  "RRHH",
  ...NOMBRES_COSTOS_FIJOS_ER,
  "Depreciación",
  "Amortización",
  "Intereses",
  "Impuestos",
];

export const construirFilasER = (leaves) => {
  const faltantes = HOJAS_ER.filter((label) => !leaves?.[label]);
  if (faltantes.length > 0) {
    throw new Error(`[construirFilasER] faltan filas hoja (10.177): ${faltantes.join(", ")}`);
  }
  const filas = { ...leaves };

  filas["A - Publicidad / Contribución"] = restarFilas(
    filas["A - Publicidad"],
    filas["A - Publicidad / Costo intermediación"],
    filas["A - Publicidad / Comisión edificios"],
    filas["A - Publicidad / Comisión comercial"],
    filas["A - Publicidad / Servicios internet"],
    filas["A - Publicidad / Software DS"],
    filas["A - Publicidad / Mantenimiento"],
    filas["A - Publicidad / Contenidos Digitales"]
  );
  filas["B - Hardware / Contribución"] = restarFilas(
    filas["B - Hardware"],
    filas["B - Hardware / Compra hardware"],
    filas["B - Hardware / Instalación"],
    filas["B - Hardware / Materiales"],
    filas["B - Hardware / Otros costos"]
  );
  filas["C - Gestión & Contenidos / Contribución"] = restarFilas(
    filas["C - Gestión & Contenidos"],
    filas["C - Gestión & Contenidos / Software DS"],
    filas["C - Gestión & Contenidos / Otros"],
    filas["C - Gestión & Contenidos / Contenidos Digitales"]
  );
  filas["D - BTL & otros / Contribución"] = restarFilas(
    filas["D - BTL & otros"],
    filas["D - BTL & otros / Comisión edificios"],
    filas["D - BTL & otros / Otros costos"]
  );

  filas["FACTURACIÓN"] = sumarFilas(
    filas["A - Publicidad"],
    filas["B - Hardware"],
    filas["C - Gestión & Contenidos"],
    filas["D - BTL & otros"]
  );

  filas["Costos Variables"] = sumarFilas(
    filas["A - Publicidad / Costo intermediación"],
    filas["A - Publicidad / Comisión edificios"],
    filas["A - Publicidad / Comisión comercial"],
    filas["A - Publicidad / Servicios internet"],
    filas["A - Publicidad / Software DS"],
    filas["A - Publicidad / Mantenimiento"],
    filas["A - Publicidad / Contenidos Digitales"],
    filas["B - Hardware / Compra hardware"],
    filas["B - Hardware / Instalación"],
    filas["B - Hardware / Materiales"],
    filas["B - Hardware / Otros costos"],
    filas["C - Gestión & Contenidos / Software DS"],
    filas["C - Gestión & Contenidos / Otros"],
    filas["C - Gestión & Contenidos / Contenidos Digitales"],
    filas["D - BTL & otros / Comisión edificios"],
    filas["D - BTL & otros / Otros costos"]
  );

  filas["Beneficio Bruto"] = restarFilas(filas["FACTURACIÓN"], filas["Costos Variables"]);

  filas["Costos Fijos"] = sumarFilas(filas["RRHH"], ...NOMBRES_COSTOS_FIJOS_ER.map((n) => filas[n]));

  filas["EBITDA"] = restarFilas(filas["Beneficio Bruto"], filas["Costos Fijos"]);

  filas["Beneficio Operativo | EBIT"] = restarFilas(filas["EBITDA"], filas["Depreciación"], filas["Amortización"]);

  filas["Beneficio Neto"] = restarFilas(filas["Beneficio Operativo | EBIT"], filas["Intereses"], filas["Impuestos"]);

  return filas;
};
